import { useState, useMemo } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { hapticMedium } from '../../utils/haptics';
import { AIRCRAFT_OPTIONS, getAircraftDisplayName } from '../../utils/aircraftTypes';

interface ManualFlightFormProps {
  flightNumber: string;
  setFlightNumber: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  dep: string;
  setDep: (v: string) => void;
  arr: string;
  setArr: (v: string) => void;
  depTime: string;
  setDepTime: (v: string) => void;
  arrTime: string;
  setArrTime: (v: string) => void;
  aircraftType: string;
  setAircraftType: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  error: string | null;
  saveLabel?: string;
}

const inputClass =
  'w-full px-3 py-2 text-sm border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-slate-700 text-slate-100 placeholder-slate-500';

export function ManualFlightForm({
  flightNumber,
  setFlightNumber,
  date,
  setDate,
  dep,
  setDep,
  arr,
  setArr,
  depTime,
  setDepTime,
  arrTime,
  setArrTime,
  aircraftType,
  setAircraftType,
  onSave,
  onCancel,
  error,
  saveLabel = 'Save Flight',
}: ManualFlightFormProps) {
  const [aircraftSearch, setAircraftSearch] = useState('');
  const [aircraftOpen, setAircraftOpen] = useState(false);

  const filteredAircraft = useMemo(() => {
    if (!aircraftSearch) return AIRCRAFT_OPTIONS.slice(0, 10);
    const q = aircraftSearch.toLowerCase();
    return AIRCRAFT_OPTIONS.filter(
      (a) => a.commonName.toLowerCase().includes(q) || a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q),
    ).slice(0, 10);
  }, [aircraftSearch]);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-400 mb-1 block">Flight #</label>
          <input
            type="text"
            value={flightNumber}
            onChange={(e) => setFlightNumber(e.target.value)}
            placeholder="LH 760"
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-400 mb-1 block">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-400 mb-1 block">From (IATA)</label>
          <input
            type="text"
            value={dep}
            onChange={(e) => setDep(e.target.value.toUpperCase())}
            placeholder="FRA"
            maxLength={3}
            className={`${inputClass} font-mono uppercase`}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-400 mb-1 block">To (IATA)</label>
          <input
            type="text"
            value={arr}
            onChange={(e) => setArr(e.target.value.toUpperCase())}
            placeholder="DXB"
            maxLength={3}
            className={`${inputClass} font-mono uppercase`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-400 mb-1 block">
            Departure Time <span className="text-slate-600">(optional)</span>
          </label>
          <input
            type="time"
            value={depTime}
            onChange={(e) => setDepTime(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-400 mb-1 block">
            Arrival Time <span className="text-slate-600">(optional)</span>
          </label>
          <input
            type="time"
            value={arrTime}
            onChange={(e) => setArrTime(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {/* Aircraft type */}
      <div className="relative">
        <label className="text-xs font-medium text-slate-400 mb-1 block">
          Aircraft <span className="text-slate-600">(optional)</span>
        </label>
        {aircraftType && !aircraftOpen ? (
          <button
            onClick={() => setAircraftOpen(true)}
            className={`${inputClass} text-left flex items-center justify-between`}
          >
            <span>{getAircraftDisplayName(aircraftType)} <span className="text-slate-500 text-xs">({aircraftType})</span></span>
            <X
              size={14}
              className="text-slate-500 hover:text-slate-300"
              onClick={(e) => { e.stopPropagation(); setAircraftType(''); }}
            />
          </button>
        ) : (
          <div>
            <div className="relative">
              <input
                type="text"
                value={aircraftSearch}
                onChange={(e) => { setAircraftSearch(e.target.value); setAircraftOpen(true); }}
                onFocus={() => setAircraftOpen(true)}
                placeholder="Search e.g. A380, 777..."
                className={inputClass}
              />
              <ChevronDown size={14} className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-transform ${aircraftOpen ? 'rotate-180' : ''}`} />
            </div>
            {aircraftOpen && (
              <div className="absolute z-10 mt-1 w-full bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {filteredAircraft.map((a) => (
                  <button
                    key={a.code}
                    onClick={() => { setAircraftType(a.code); setAircraftSearch(''); setAircraftOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-600 flex items-center justify-between"
                  >
                    <span className="text-slate-100">{a.commonName}</span>
                    <span className="text-xs text-slate-500 font-mono">{a.code}</span>
                  </button>
                ))}
                {filteredAircraft.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No match found</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex gap-2 mt-1">
        <button
          onClick={() => { hapticMedium(); onSave(); }}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark"
        >
          <Check size={16} />
          {saveLabel}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
